import { MouseEventHandler, useCallback, useState } from 'react';

import { Grid, GridItem } from '@chakra-ui/react';
import cn from 'clsx';
import { formatInTimeZone } from 'date-fns-tz';
import truncate from 'lodash/truncate';

import {
  Box,
  Button,
  defaultPlaceholder,
  Img,
  Link,
  Text,
} from '@r-client/shared/ui/core';
import { SafeHTML } from '@r-client/shared/util/components';

import { TGetEventsEvent, TGetTagEvent } from '../../types';
import { CalendarDate, Day, Month } from '../calendar-date/calendar-date';
import { SEOStructuredData } from '../seo-structured-data';
import { EventCardFooter } from './event-card-footer/event-card-footer';
import { EventCardHeader } from './event-card-header/event-card-header';
import { HostList } from './host-list/host-list';

import styles from './event-card.module.scss';

export interface IEventCardProps {
  className?: string;
  event: TGetEventsEvent | TGetTagEvent;
  onWindowOpen?: (url: string) => void;
  onModalOpen?: (id: string) => void;
}

export enum E_TEST_IDS {
  eventCard = 'eventCard',
  calendarDate = 'calendarDate',
  eventHours = 'eventHours',
  seoData = 'seoData',
  hostList = 'hostList',
}

const headerTestIds = { eventHours: E_TEST_IDS.eventHours };

const MAX_BODY_VISIBLE_LENGTH = 180;
const BODY_TRUNCATE_CONFIG = { length: MAX_BODY_VISIBLE_LENGTH };

const getBodyPreview = (body: string) => truncate(body, BODY_TRUNCATE_CONFIG);

export function EventCard({
  className,
  event,
  onModalOpen,
  onWindowOpen,
}: IEventCardProps) {
  const [isHover, setIsHover] = useState(false);
  const handleMouseEnter = useCallback(() => setIsHover(true), []);
  const handleMouseLeave = useCallback(() => setIsHover(false), []);

  const handleCtaButtonClick: MouseEventHandler<HTMLElement> = (e) => {
    if (!event.cta) return;

    if (event.cta.rsvp) {
      e.preventDefault();
      e.stopPropagation();
      onModalOpen?.(event.id);
      return;
    }

    if (event.cta.url) {
      e.preventDefault();
      e.stopPropagation();
      onWindowOpen?.(event.cta.url);
    }
  };

  const showFooter = Boolean(
    event.cta || (event.bodySanitized && event.xHosts?.length)
  );

  const beginAtDay = formatInTimeZone(event.beginAt, event.timezone, 'd');

  const beginAtMonth = formatInTimeZone(event.beginAt, event.timezone, 'MMM');

  return (
    <Img.HoverHandle
      data-testid={E_TEST_IDS.eventCard}
      as="article"
      className={cn(className, styles.card)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Link href={`/events/${event.slug}`} variant={Link.variants.dark}>
        <div className={styles.content}>
          <Grid templateColumns="repeat(24, 1fr)" gap={0}>
            <GridItem colSpan={{ base: 24, sm: 7 }} className={styles.imageBox}>
              <Img
                alt={event.title}
                src={event.coverPhotoUrl ?? defaultPlaceholder}
                fullWidth
                borderRadius="3"
                hoverEffect="zoom"
                aspectRatio="16/9"
                loading="lazy"
              />
            </GridItem>

            <GridItem colSpan={{ base: 4, sm: 2 }}>
              <CalendarDate
                className={styles.calendarDate}
                dataTestId={E_TEST_IDS.calendarDate}
              >
                <Month className={styles.month}>{beginAtMonth}</Month>
                <Day
                  className={cn(styles.day, {
                    [styles.dayPast]: event.past,
                  })}
                >
                  {beginAtDay}
                </Day>
              </CalendarDate>
            </GridItem>

            <GridItem colSpan={{ base: 20, sm: 14, md: 13 }}>
              <EventCardHeader event={event} testIds={headerTestIds}>
                {/* TODO https://linear.app/republic/issue/RCE-1324/update-eventseventtitle-output
                    remove SafeHTML after fixind the issue
                 */}
                <SafeHTML sanitizedHtml={event.title}>
                  <Text type="h4" as="p" />
                </SafeHTML>
              </EventCardHeader>

              {!!event.bodySanitized && (
                <Text
                  weight="300"
                  type="bodyBig"
                  color="gray6"
                  className={styles.bodyText}
                >
                  {getBodyPreview(event.bodySanitized)}
                </Text>
              )}
            </GridItem>

            <GridItem colSpan={{ base: 0, sm: 1, md: 2 }} />
          </Grid>
        </div>

        {!!showFooter && (
          <EventCardFooter>
            {!!event.cta && (
              <Box m="0 0.5brm 0 0">
                <Button
                  className={styles.ctaButton}
                  size="small"
                  type="primary"
                  onClick={handleCtaButtonClick}
                >
                  {event.cta.text}
                </Button>
              </Box>
            )}

            {!!event.xHosts?.length && (
              <HostList
                hosts={event.xHosts}
                forceExpand={isHover}
                dataTestId={E_TEST_IDS.hostList}
              />
            )}
          </EventCardFooter>
        )}
      </Link>

      <SEOStructuredData data={event} dataTestId={E_TEST_IDS.seoData} />
    </Img.HoverHandle>
  );
}
